import { useState, useCallback, useEffect } from 'react';
import { 
  RecaptchaVerifier, 
  linkWithPhoneNumber, 
  PhoneAuthProvider,
  linkWithCredential
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';

export const useFirebasePhoneAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null);

  // Initialize reCAPTCHA
  const initializeRecaptcha = useCallback(async (containerId: string) => {
    try {
      console.log('Initializing reCAPTCHA for container:', containerId);
      
      // Clear any existing verifier
      if (recaptchaVerifier) {
        console.log('Clearing existing reCAPTCHA verifier');
        recaptchaVerifier.clear();
      }

      // Check if container exists
      const container = document.getElementById(containerId);
      if (!container) {
        console.error('reCAPTCHA container not found:', containerId);
        setError('reCAPTCHA container not found. Please refresh the page.');
        return null;
      }

      console.log('Creating new reCAPTCHA verifier');
      console.log('Firebase auth object:', auth);
      console.log('Container ID:', containerId);
      
      // Try different reCAPTCHA configurations
      let verifier;

        // First try with minimal configuration
        verifier = new RecaptchaVerifier(auth, containerId, {
          size: 'normal'
        });
        console.log('reCAPTCHA verifier created with minimal config');
      

      console.log('reCAPTCHA verifier created successfully');
      

        await verifier.render();
        console.log('reCAPTCHA widget rendered successfully');

      
      setRecaptchaVerifier(verifier);
      return verifier;
    } catch (error: any) {
      console.error('Failed to initialize reCAPTCHA:', error);
      setError('Failed to initialize verification. Please refresh the page.');
      return null;
    }
  }, [recaptchaVerifier]);

  // Send verification code
  const sendVerificationCode = useCallback(async (phoneNumber: string) => {
    setLoading(true);
    setError(null);

    try {
      // Check if user is already signed in
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No user is currently signed in. Please sign in first.');
      }

      // Initialize reCAPTCHA if not already done
      let appVerifier = recaptchaVerifier;
      if (!appVerifier) {
        appVerifier = await initializeRecaptcha('recaptcha-container');
        if (!appVerifier) {
          throw new Error('Failed to initialize verification');
        }
      }
      
      // Send verification code using linkWithPhoneNumber
      const confirmationResult = await linkWithPhoneNumber(currentUser, phoneNumber, appVerifier);
      setVerificationId(confirmationResult.verificationId);
      
      return { success: true, confirmationResult };
    } catch (error: any) {
      console.error('Failed to send verification code:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to send verification code';
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format. Please use international format (e.g., +1234567890)';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many attempts. Please try again later.';
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please try again later.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please sign in again to link your phone number.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [recaptchaVerifier, initializeRecaptcha]);

  // Verify code
  const verifyCode = useCallback(async (code: string) => {
    setLoading(true);
    setError(null);

    try {
      if (!verificationId) {
        throw new Error('No verification ID found. Please send a verification code first.');
      }

      // Check if user is already signed in
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No user is currently signed in. Please sign in first.');
      }

      // Create credential
      const credential = PhoneAuthProvider.credential(verificationId, code);
      
      // Link the phone credential to the existing user account
      const result = await linkWithCredential(currentUser, credential);
      
      // Force refresh the Firebase token after phone linking
      const idToken = await result.user.getIdToken(true);
      
      // Update axios default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
      
      // Get updated user data from backend
      const response = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      
      const updatedUser = response.data.data.user;
      
      // Update the complete auth state
      useAuthStore.setState({
        user: updatedUser,
        token: idToken,
        isAuthenticated: true,
        isLoading: false,
        pendingTermsApproval: !updatedUser.termsAcceptedAt
      });
      
      return { success: true, idToken, user: result.user };
    } catch (error: any) {
      console.error('Failed to verify code:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to verify code';
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Invalid verification code. Please check and try again.';
      } else if (error.code === 'auth/invalid-verification-id') {
        errorMessage = 'Verification session expired. Please request a new code.';
      } else if (error.code === 'auth/credential-already-in-use') {
        errorMessage = 'This phone number is already linked to another account.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please sign in again to link your phone number.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [verificationId]);

  // Reset state
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    //setVerificationId(null);
    
    // Clear reCAPTCHA
    if (recaptchaVerifier) {
      try {
        // Check if the verifier is still valid before clearing
        if (recaptchaVerifier && typeof recaptchaVerifier.clear === 'function') {
          recaptchaVerifier.clear();
        }
      } catch (error) {
        console.log('reCAPTCHA clear error (ignored):', error);
      }
      setRecaptchaVerifier(null);
    }
  }, [recaptchaVerifier]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifier) {
        try {
          // Check if the verifier is still valid before clearing
          if (recaptchaVerifier && typeof recaptchaVerifier.clear === 'function') {
            recaptchaVerifier.clear();
          }
        } catch (error) {
          // Ignore cleanup errors as they don't affect functionality
          console.log('reCAPTCHA cleanup error (ignored):', error);
        }
      }
    };
  }, [recaptchaVerifier]);

  return {
    loading,
    error,
    verificationId,
    sendVerificationCode,
    verifyCode,
    reset,
    initializeRecaptcha
  };
}; 